import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SEOHead from "../components/SEOHead";

const PrivacyPolicy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const privacyStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "プライバシーポリシー",
    description:
      "わかば接骨院のプライバシーポリシー。個人情報の取り扱いについて説明しています。",
    url: "https://wakabaseto.jp/privacy-policy",
  };

  return (
    <>
      <SEOHead
        title="プライバシーポリシー"
        description="わかば接骨院のプライバシーポリシー。個人情報の取り扱いについて説明しています。"
        keywords="プライバシーポリシー,個人情報保護,わかば接骨院"
        canonical="/privacy-policy"
        structuredData={privacyStructuredData}
      />
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: "primary.light",
            py: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 3, sm: 4, md: 6 },
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={isMobile ? "h2" : "h1"}
              gutterBottom
              align="center"
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: isTablet ? "2.25rem" : "2.5rem",
                  md: "3rem",
                },
              }}
            >
              プライバシーポリシー
            </Typography>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              align="center"
              sx={{
                mt: { xs: 1, md: 2 },
                color: "text.primary",
                fontSize: isTablet ? "1.1rem" : undefined,
              }}
            >
              個人情報保護方針
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="lg">
          <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, mb: 6 }}>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              gutterBottom
              sx={{ mb: 4 }}
            >
              プライバシーポリシー
            </Typography>

            <Typography variant="body1" paragraph>
              わかば接骨院（以下「当院」）は、お客様の個人情報の保護を最重要事項と考え、以下のプライバシーポリシーに基づいて個人情報の取り扱いを行います。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              1. 個人情報の収集について
            </Typography>
            <Typography variant="body1" paragraph>
              当院では、以下の目的で個人情報を収集いたします：
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                診療サービスの提供
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                お問い合わせへの対応
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                予約管理
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                施術効果の追跡・改善
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                当院からのお知らせ・案内
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              2. 収集する個人情報の種類
            </Typography>
            <Typography variant="body1" paragraph>
              当院では、以下の個人情報を収集いたします：
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                氏名、住所、電話番号
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                生年月日、性別
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                診療情報（症状、施術歴、薬剤情報等）
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                保険情報
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              3. 個人情報の利用について
            </Typography>
            <Typography variant="body1" paragraph>
              収集した個人情報は、収集目的の範囲内でのみ利用いたします。利用目的以外での利用はいたしません。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              4. 個人情報の管理について
            </Typography>
            <Typography variant="body1" paragraph>
              当院では、個人情報の漏洩、滅失、き損の防止その他の個人情報の安全管理のために必要かつ適切な措置を講じます。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              5. 個人情報の第三者提供について
            </Typography>
            <Typography variant="body1" paragraph>
              当院では、以下の場合を除き、個人情報を第三者に提供いたしません：
            </Typography>
            <Box component="ul" sx={{ pl: 4, mb: 3 }}>
              <Typography component="li" variant="body1" paragraph>
                お客様の同意がある場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                法令に基づく場合
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                人の生命、身体または財産の保護のために必要な場合
              </Typography>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              6. 個人情報の開示・訂正・利用停止について
            </Typography>
            <Typography variant="body1" paragraph>
              お客様は、当院に対して、ご自身の個人情報の開示、訂正、利用停止を求めることができます。これらのご要望については、お問い合わせフォームまたはお電話にてご連絡ください。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              7. クッキー（Cookie）の使用について
            </Typography>
            <Typography variant="body1" paragraph>
              当院のウェブサイトでは、お客様により良いサービスを提供するため、クッキーを使用することがあります。クッキーの使用を望まない場合は、ブラウザの設定でクッキーを無効にすることができます。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              8. プライバシーポリシーの変更について
            </Typography>
            <Typography variant="body1" paragraph>
              当院は、必要に応じてこのプライバシーポリシーを変更することがあります。変更があった場合は、当院のウェブサイトでお知らせいたします。
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              9. お問い合わせ
            </Typography>
            <Typography variant="body1" paragraph>
              個人情報の取り扱いに関するお問い合わせは、以下の連絡先までお願いいたします：
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>わかば接骨院</strong>
              <br />
              〒489-0809
              <br />
              愛知県瀬戸市共栄通3-5
              <br />
              TEL: 0561-83-3833
              <br />
              受付時間:
              平日（水曜以外）午前9時から12時・午後4時から8時、水曜・土曜は午前9時から12時
            </Typography>

            <Typography variant="body2" sx={{ mt: 4, color: "text.secondary" }}>
              制定日: 2024年12月14日
              <br />
              最終更新日: 2024年12月14日
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
